// Backward source-walk helper shared by the cursor-relative scanners that must
// reach the syntax *before* the caret (dotted-base extraction, Oracle `:NEW.`
// colon-prefix detection). A `/* … */` block comment is syntactically
// whitespace, so `t./* c */col` / `:NEW./* caret */col` must still resolve the
// dotted base when the caret sits just past the comment — these char-walks skip
// whitespace but historically stopped at the comment's trailing `/`.

import { isWhitespace } from './sourceScan';

/**
 * Walk backward from `pos`, skipping whitespace and any trailing `/* … *​/`
 * block comments. Stops at `limit`. Returns the right edge of the first
 * non-whitespace, non-block-comment character.
 *
 * Line comments are intentionally NOT skipped: a `--` comment runs to
 * end-of-line, so a caret on the same line sits inside it, and a caret on the
 * next line is already reached by the whitespace (newline) skip.
 */
export function skipWhitespaceAndCommentsBackward(
  source: string,
  pos: number,
  limit: number,
): number {
  let p = Math.min(pos, source.length);
  for (;;) {
    const before = p;
    while (p > limit && isWhitespace(source.charCodeAt(p - 1))) p -= 1;
    // Trailing block-comment close `*​/` at [p-2]=`*`, [p-1]=`/`.
    if (
      p - 2 >= limit &&
      source.charCodeAt(p - 1) === 0x2f /* / */ &&
      source.charCodeAt(p - 2) === 0x2a /* * */
    ) {
      // Scan back for the matching opener `/​*` at [k-1]=`/`, [k]=`*`.
      let k = p - 3;
      while (
        k >= limit + 1 &&
        !(source.charCodeAt(k - 1) === 0x2f /* / */ && source.charCodeAt(k) === 0x2a /* * */)
      ) {
        k -= 1;
      }
      if (
        k >= limit + 1 &&
        source.charCodeAt(k - 1) === 0x2f /* / */ &&
        source.charCodeAt(k) === 0x2a /* * */
      ) {
        p = k - 1;
      } else {
        break; // no opener within range — leave `p` put
      }
    }
    if (p === before) break;
  }
  return p;
}
